import type { Permission } from '@fieldops/shared'
import type { TourStep } from './types.js'

/**
 * The welcome tour. Decision 115.
 *
 * It points at the rail (or, on a phone, the tabs), because those are on
 * every screen and do not move; each step also opens its screen behind the
 * card, so the words describe what the person can see. A step is skipped for
 * somebody whose role cannot open that screen, the same rule the rail uses.
 *
 * Short on purpose: one idea a step, two sentences at most. The detail lives
 * in the help centre, which the last step points at.
 */

const rail = (path: string) => `[data-guide="rail"] a[href="${path}"]`
const tab = (path: string) => `nav[aria-label="Main"] a[href="${path}"]`

export const DESK_TOUR: TourStep[] = [
  {
    id: 'today',
    title: 'Today',
    body: 'Where every morning starts: how the day is going, every booking, and the short list of things waiting on the office.',
    target: rail('/'),
    route: '/',
  },
  {
    id: 'jobs',
    title: 'Jobs',
    body: 'Every job by the stage it is at — quoting, booking, under way, to invoice. Click any job and its card opens over the screen you are on.',
    target: rail('/jobs'),
    route: '/jobs',
    permission: 'JOB_VIEW',
  },
  {
    id: 'board',
    title: 'The Board',
    body: 'Book, move and confirm visits for each technician. Drag a waiting job onto a row to book it; the Booking desk works through the jobs still to book.',
    target: rail('/board'),
    route: '/board',
    permission: 'SCHEDULE_VIEW',
  },
  {
    id: 'inbox',
    title: 'Inbox',
    body: 'Email and texts with customers, matched to their clients and jobs. A green square on the rail means something is unread.',
    target: rail('/inbox'),
    route: '/inbox',
    permission: 'INBOX_VIEW',
  },
  {
    id: 'clients',
    title: 'Clients and agreements',
    body: 'Everyone you work for, their sites and their people. Agreements, just below, hold the services that repeat.',
    target: rail('/clients'),
    route: '/clients',
    permission: 'CLIENT_VIEW',
  },
  {
    id: 'billing',
    title: 'Billing',
    body: 'Open quotes, finished work to invoice, and money owed — each with its next step, so nothing is left sitting.',
    target: rail('/billing'),
    route: '/billing',
    permission: 'INVOICE_VIEW',
  },
  {
    id: 'settings',
    title: 'Settings',
    body: 'People, the business’s details and your own account. Your account is also where this tour lives if you want it again.',
    target: rail('/settings'),
    route: '/settings',
  },
  {
    id: 'help',
    title: 'Help is one key away',
    body: 'Press ? on any screen, or click Help, for the guides to what you are looking at. When EcoPest changes, What’s new tells you what is different.',
    target: '[data-guide="help-button"]',
  },
]

export const FIELD_TOUR: TourStep[] = [
  {
    id: 'today',
    title: 'Today',
    body: 'Your next job is at the top, with Call and the button that moves it on — on the way, on site, done. Tap it for directions and everything else.',
    target: tab('/'),
    route: '/',
  },
  {
    id: 'jobs',
    title: 'Jobs',
    body: 'Your jobs, coming up and done. Tap one for the notes, the site, photos and the report.',
    target: tab('/jobs'),
    route: '/jobs',
    permission: 'JOB_VIEW',
  },
  {
    id: 'team',
    title: 'Team',
    body: 'Where everyone is today, and the jobs that still need a technician.',
    target: tab('/team'),
    route: '/team',
    permission: 'SCHEDULE_VIEW_ALL',
  },
  {
    id: 'inbox',
    title: 'Inbox',
    body: 'Messages from the office and from customers about your jobs.',
    target: tab('/inbox'),
    route: '/inbox',
    permission: 'INBOX_VIEW',
  },
  {
    id: 'me',
    title: 'Me',
    body: 'Your licences, the rest of the app, and Help: the guides, this tour again, and what’s new.',
    target: tab('/me'),
    route: '/me',
  },
]

export function tourFor(
  field: boolean,
  can: (...required: Permission[]) => boolean,
): TourStep[] {
  return (field ? FIELD_TOUR : DESK_TOUR).filter((step) => !step.permission || can(step.permission))
}
